type inputType = string | object;

/**
 * Ensure some object is a coerced to a string
 * The old dependency underscore.string had a guard for null and undefined.
 * @param {*} object
 * @return {String}
 */
const makeString = (object: inputType) => (object == null ? "" : `${object}`);

/**
 * Check if a string contains any characters
 * White space and New line are escaped.
 * @param {String} str
 * @return {boolean}
 */
export function isBlank(str: inputType) {
    str = makeString(str);
    if (str.length === 0) {
        return true;
    }

    return str.trim().length === 0;
}

/**
 * Verify that a string his in the beginning of an other
 * @param {String} str
 * @param {String} searchString The string to search
 * @param {Number} [position=0] The position to start
 * @return {boolean}
 */
export function startsWith(str: inputType, searchString: string, position: number = 0) {
    // ht: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/startsWith
    return makeString(str).substr(!position || position < 0 ? 0 : +position, searchString.length) === searchString;
}

/**
 * Verify that a string his included in a string
 * @param {String} str
 * @param {String} searchString The string to search
 * @param {Number} [position=0] The position to start
 * @return {boolean}
 */
export function contains(str: inputType, searchString: string, position: number = 0) {
    str = makeString(str);
    // ht: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes
    if (position + searchString.length > str.length) {
        return false;
    } else {
        return str.indexOf(searchString, position) !== -1;
    }
}

/**
 * Capitalize the first letter of a String
 * @param {String} str
 * @return {String}
 */
export function capitalize(str: inputType) {
    str = makeString(str);
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

/**
 * Capitalize every first character of each word in a sentence
 * @param {String} str
 * @return {String}
 */
export function titleize(str: inputType) {
    return makeString(str).toLowerCase()
        .replace(
            /(?:^|\s|-)\S/g,
            (c) => c.toUpperCase()
        );
}

/**
 * Converts underscored or dasherized string to a camelized one.
 * @param {String} str
 * @return {String}
 */
export function camelize(str: inputType) {
    return makeString(str).trim()
        .replace(
            /[-_\s]+(.)?/g,
            (match, c) => (match !== "" ? c.toUpperCase() : "")
        );
}

/**
 * Converts underscord or dasherized string to a camelized one.
 * @param {String} str
 * @return {String}
 */
export function dasherize(str: inputType) {
    return makeString(str).replace(/([A-Z])/g, "-$1")
        .replace(/[-_\s]+/g, "-")
        .toLowerCase();
}

/**
 * Delete whitespaces at the end of a String
 * @param {String} str
 * @return {String}
 */
export function rtrim(str: inputType) {
    str = makeString(str);
    return str.replace(/\s*$/, "");
}

/**
 * Delete whitespaces at the beginning and the end of a String
 * @param {String} str
 * @return {String}
 */
export function trim(str: inputType) {
    return makeString(str).trim();
}

export default class StringUtils {
    static isBlank = isBlank;
    static startsWith = startsWith;
    static contains = contains;
    static capitalize = capitalize;
    static titleize = titleize;
    static camelize = camelize;
    static dasherize = dasherize;
    static rtrim = rtrim;
    static trim = trim;
}



// WEBPACK FOOTER //
// ./src/shared/util/StringUtils.ts